import * as styles from "./ClientEditorHeader.css";

import { useAtomValue } from "jotai";
import type { JSX } from "react";

import { Avatar } from "@frontend/components/atoms/Avatar";
import { Badge } from "@frontend/components/atoms/Badge";
import { Heading } from "@frontend/components/atoms/Heading";
import { Icon } from "@frontend/components/atoms/Icon";
import { TruncateMiddle } from "@frontend/components/atoms/TruncateMiddle";
import {
  PageHeader,
  PageHeaderContent,
  PageHeaderHeading,
  PageHeaderMeta,
  type PageHeaderVariant,
  PageHeaderVisual,
} from "@frontend/components/organisms/PageHeader";
import { useLingui } from "@lingui/react/macro";

import { clientEditorAtom } from "../stores/editor";

export type ClientEditorHeaderProps = {
  variant?: PageHeaderVariant;
};

/**
 * What the legacy page title card shows above the form: the client, where it
 * stands, and who last touched it.
 */
export function ClientEditorHeader({
  variant = "card",
}: ClientEditorHeaderProps): JSX.Element {
  const { t } = useLingui();

  const { client, contact } = useAtomValue(clientEditorAtom);

  // Named rather than read inline, so lingui keeps a readable placeholder.
  const modifiedBy = client?.modificationUsername;
  const modifiedAt = client?.modificationDate?.slice(0, 10);

  const label = client?.displayName ?? contact?.displayName ?? null;

  return (
    <PageHeader variant={variant}>
      <PageHeaderVisual variant="plain">
        {/* The square sizes the avatar, never the reverse, so a dialog header
            keeps its height. Inline, so it wins over the recipe whatever the
            order the stylesheets land in. */}
        <Avatar
          size="md"
          style={{ width: "100%", height: "100%" }}
          url={contact?.avatarUrl ?? undefined}
          fallback={label ? initials(label) : <Icon name="Briefcase" />}
        />
      </PageHeaderVisual>

      <PageHeaderContent>
        <PageHeaderHeading>
          <Heading name={label ?? t`New client`} suffix={client?.id} />

          {client?.statusLabel ? (
            <Badge variant="neutral">{client.statusLabel}</Badge>
          ) : null}
        </PageHeaderHeading>

        {modifiedBy && modifiedAt ? (
          <PageHeaderMeta>
            <TruncateMiddle
              className={styles.created}
              text={t`Last changed by ${modifiedBy} on ${modifiedAt}`}
            />
          </PageHeaderMeta>
        ) : null}
      </PageHeaderContent>
    </PageHeader>
  );
}

/** Two letters for the avatar: the first of the first two words. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join("");
}
